import type { FeatureFlagKey } from '../../../../core/services/feature-flags-viewer.service';

/**
 * The catalogue flag keys this panel gates on — checked at COMPILE TIME.
 *
 * WHY THIS FILE EXISTS (read before "simplifying" it back to string literals).
 *
 * `*imFeatureFlag` declares `input.required<FeatureFlagKey>()`, which LOOKS
 * like it type-checks the flag name. It does not. The union in
 * `core/services/feature-flags-viewer.service.ts` ends with:
 *
 *     | (string & {});
 *
 * That is the standard "literal autocomplete without collapsing to string"
 * trick, and its cost is that EVERY string is assignable to `FeatureFlagKey`.
 * So `*imFeatureFlag="'maps.wayfindng'"` compiles clean, the directive asks
 * the viewer flag set for a key nobody publishes, gets `false`, and silently
 * renders nothing. The failure mode is a CTA that is simply absent — no error,
 * no log, nothing to notice in review. Both gates on this panel guard
 * navigation targets that redirect to the home page when their flag is off,
 * so a typo does not degrade to "ungated": it degrades to "permanently gone".
 *
 * There is no runtime flag catalogue on the frontend to check against. Unlike
 * actions — which have `core/constants/action-catalogue.ts`, a real `Action`
 * const object with a drift spec — the flag keys exist ONLY as members of that
 * TypeScript union; the admin surface reads its list from the server at
 * runtime (`customerFeatureFlags`). So the union is the only catalogue there
 * is, and `CatalogueFlag` below is how we read it without the escape hatch.
 *
 * `CatalogueFlag` distributes over `FeatureFlagKey` and drops any member that
 * `string` itself is assignable to — which is exactly the `(string & {})` arm
 * and nothing else. What survives is the literal union alone. A typo then
 * fails `satisfies` with TS2820 ("Did you mean...?"), caught by `ng build`.
 *
 * Forward-compatible: if someone deletes `| (string & {})` upstream (the real
 * fix, out of this lane), `CatalogueFlag` becomes an identity mapping and
 * everything here keeps working unchanged.
 */
type CatalogueFlag = FeatureFlagKey extends infer K
  ? K extends string
    ? string extends K
      ? never
      : K
    : never
  : never;

/**
 * Bind template gates to these, never to a bare string.
 *
 * - `mapsWayfinding` guards `/map` (Change desk, and the office presence tile's
 *   non-template path via `canBookDesk`).
 * - `platformMyWeek` guards `/home/my-week` (Plan the rest of your week).
 * - `helpCentre` guards `/help` (Help centre, and Get Help beneath it).
 * - `helpSupport` guards Get Help (Contact support), on top of `helpCentre`.
 *
 * `profile-panel-flags.spec.ts` additionally asserts at RUNTIME that both
 * strings are still members of the union in the service source, so an upstream
 * rename or removal fails a test rather than quietly hiding a CTA.
 */
export const PROFILE_PANEL_FLAGS = {
  mapsWayfinding: 'maps.wayfinding',
  platformMyWeek: 'platform.my-week',
  helpCentre: 'help.centre',
  helpSupport: 'help.support',
} as const satisfies Record<string, CatalogueFlag>;

export type ProfilePanelFlag =
  (typeof PROFILE_PANEL_FLAGS)[keyof typeof PROFILE_PANEL_FLAGS];
